import * as React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";

type Props = {
  handlePageChange: (page: string) => void;
  nextPage?: string;
  backPage?: string;
};

export default function AwareIntroScreen({
  handlePageChange,
  nextPage = "AwareQuestions",
  backPage = "JourneyPillars",
}: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        width: "100%",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(rgba(8,8,8,0.78), rgba(8,8,8,0.86)),
          url("/scenarioBgs/threats.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 780px)", md: "min(78vw, 860px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6, md: 7 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={3.5} alignItems="center">
          <Chip
            label={isDE ? "BEWUSST" : "AWARE"}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              backgroundColor: "rgba(255,255,255,0.08)",
              fontWeight: 800,
              letterSpacing: "0.08em",
            }}
          />

          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.8rem", sm: "2.3rem", md: "2.7rem" },
              fontWeight: 900,
              letterSpacing: "0.06em",
              lineHeight: 1.15,
            }}
          >
            {isDE ? "Stärke beginnt mit Klarheit." : "Strength begins with clarity."}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.82)",
              fontSize: { xs: "1rem", sm: "1.15rem" },
              lineHeight: 1.7,
              maxWidth: 620,
            }}
          >
            {isDE
              ? "In diesem ersten Teil entsteht dein persönlicher Lab Score. Du beantwortest kurze Fragen zu Risiken, Informationsverhalten, Vorbereitung und deiner Verfügbarkeit in einer Krise."
              : "In this first part, your personal Lab Score begins. You will answer short questions about risks, information behaviour, preparedness and your availability in a crisis."}
          </Typography>

          <Box
            sx={{
              width: "100%",
              maxWidth: 620,
              borderRadius: 4,
              p: { xs: 2.5, sm: 3 },
              backgroundColor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                mb: 1.25,
                fontSize: { xs: "1rem", sm: "1.1rem" },
              }}
            >
              {isDE ? "Was passiert hier?" : "What happens here?"}
            </Typography>

            <Stack spacing={1}>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.55 }}>
                {isDE
                  ? "• BEWUSST erstellt deinen Ausgangswert auf einer 0–60 Baseline-Skala."
                  : "• AWARE creates your starting point on a 0–60 baseline scale."}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.55 }}>
                {isDE
                  ? "• Danach verbesserst du denselben Score durch praktische BEREIT-Stationen."
                  : "• After that, you improve the same score through practical PREPARE stations."}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.55 }}>
                {isDE
                  ? "• BEWUSST ist dein Ausgangspunkt; mit BEREIT wächst daraus der Lab Score."
                  : "• AWARE is your starting point; PREPARE grows it into the Lab Score."}
              </Typography>
            </Stack>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(backPage)}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                borderColor: "rgba(255,255,255,0.4)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {isDE ? "Zurück" : "Back"}
            </Button>

            <Button
              variant="contained"
              onClick={() => handlePageChange(nextPage)}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                },
              }}
            >
              {isDE ? "BEWUSST starten" : "Start AWARE"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
